/* Auto-playing stacking slideshow (per the Figma dev note): reveal one
   card every `step` ms with hard cuts, no transitions. Cards accumulate;
   once all are showing, hold, then reset and loop. With 12 cards that's
   11 x 1.3s + 1.7s = the note's 16s cycle.

   `shown` is the number of cards revealed so far; the template binds each
   card's `invisible` class to it. That class is Tailwind's
   `visibility: hidden` - NOT `x-show`/`display: none`, which would take
   the cards out of layout and stop the browser lazy-loading their images
   until the moment each one is revealed.

   Cards render visible before Alpine boots, so no-JS (and
   prefers-reduced-motion) shows the completed pile.

   The cycle only runs while the stage is near/on screen and the tab is in
   the foreground, so it isn't playing to nobody at the bottom of the
   page. */
export function slideshow(count = 0) {
  return {
    count,
    shown: count,
    step: 1300,
    hold: 1700,
    lead: 600,
    timer: null,
    inView: false,
    observer: null,
    onVisibility: null,

    init() {
      if (
        this.count < 2 ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        return;
      }

      this.shown = 0;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.inView = entry.isIntersecting;
          this.sync();
        },
        /* start a screenful early so the first cards are already down by
           the time the stage is actually scrolled to */
        { rootMargin: `${this.lead}px 0px` },
      );
      this.observer.observe(this.$root);

      this.onVisibility = () => this.sync();
      document.addEventListener("visibilitychange", this.onVisibility);
    },

    /* run only when near the viewport and the tab is in the foreground */
    sync() {
      this.inView && !document.hidden ? this.start() : this.stop();
    },

    start() {
      if (!this.timer) {
        this.tick();
      }
    },

    tick() {
      this.shown += 1;

      this.timer =
        this.shown < this.count
          ? setTimeout(() => this.tick(), this.step)
          : setTimeout(() => {
              this.shown = 0;
              this.tick();
            }, this.hold);
    },

    stop() {
      clearTimeout(this.timer);
      this.timer = null;
    },

    destroy() {
      this.stop();
      this.observer?.disconnect();
      document.removeEventListener("visibilitychange", this.onVisibility);
    },
  };
}
